/**
 * Sentry options a deployment can set. The application owns every other
 * option, including the transport and the hooks that redact events.
 */
export interface SentryDeploymentConfig {
    dsn: string;
    environment?: string;
    release?: string;
    sampleRate?: number;
    tracesSampleRate?: number;
}

/**
 * Configuration that a deployment embeds in the page as JSON.
 */
export interface DeploymentConfig {
    /** The default Infinity hostname of a native deployment. */
    hostname?: string;
    sentry?: SentryDeploymentConfig;
}

// The deployed configurations are a few hundred characters long.
const MAX_DEPLOYMENT_CONFIG_LENGTH = 16 * 1024;
// Limits of the Sentry ingestion API.
const MAX_SENTRY_ENVIRONMENT_LENGTH = 64;
const MAX_SENTRY_RELEASE_LENGTH = 200;

// A DNS hostname: letters, digits and inner hyphens in labels of 1 to 63
// characters, and at most 253 characters, without a scheme, port or path.
const HOSTNAME_PATTERN =
    /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/i;

const DEPLOYMENT_CONFIG_KEYS: ReadonlySet<string> = new Set([
    'hostname',
    'sentry',
]);
const SENTRY_KEYS: ReadonlySet<string> = new Set([
    'dsn',
    'environment',
    'release',
    'sampleRate',
    'tracesSampleRate',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const assertKnownKeys = (
    value: Record<string, unknown>,
    allowedKeys: ReadonlySet<string>,
    path: string,
) => {
    for (const key of Object.keys(value)) {
        if (!allowedKeys.has(key)) {
            throw new TypeError(
                `${path} contains the unsupported key ${JSON.stringify(key)}`,
            );
        }
    }
};

const parseDsn = (value: unknown): string => {
    if (typeof value !== 'string') {
        throw new TypeError('sentry.dsn must be a URL');
    }
    let url: URL;
    try {
        url = new URL(value);
    } catch (error: unknown) {
        throw new TypeError('sentry.dsn must be a valid URL', {cause: error});
    }
    if (url.protocol !== 'https:') {
        throw new TypeError('sentry.dsn must use https');
    }
    return value;
};

const parseOptionalHostname = (value: unknown): string | undefined => {
    if (value === undefined) {
        return undefined;
    }
    if (typeof value !== 'string' || !HOSTNAME_PATTERN.test(value)) {
        throw new TypeError('hostname must be a DNS hostname');
    }
    return value;
};

const parseOptionalString = (
    value: unknown,
    path: string,
    maxLength: number,
): string | undefined => {
    if (value === undefined) {
        return undefined;
    }
    if (
        typeof value !== 'string' ||
        value.length === 0 ||
        value.length > maxLength
    ) {
        throw new TypeError(
            `${path} must be a string of 1 to ${maxLength} characters`,
        );
    }
    return value;
};

const parseOptionalRate = (
    value: unknown,
    path: string,
): number | undefined => {
    if (value === undefined) {
        return undefined;
    }
    if (typeof value !== 'number' || !(value >= 0 && value <= 1)) {
        throw new TypeError(`${path} must be a number from 0 to 1`);
    }
    return value;
};

const parseSentryConfig = (value: unknown): SentryDeploymentConfig => {
    if (!isRecord(value)) {
        throw new TypeError('sentry must be a JSON object');
    }
    assertKnownKeys(value, SENTRY_KEYS, 'sentry');

    const environment = parseOptionalString(
        value.environment,
        'sentry.environment',
        MAX_SENTRY_ENVIRONMENT_LENGTH,
    );
    const release = parseOptionalString(
        value.release,
        'sentry.release',
        MAX_SENTRY_RELEASE_LENGTH,
    );
    const sampleRate = parseOptionalRate(value.sampleRate, 'sentry.sampleRate');
    const tracesSampleRate = parseOptionalRate(
        value.tracesSampleRate,
        'sentry.tracesSampleRate',
    );

    // Omit absent options, so they do not replace the application defaults,
    // such as the release, when the options are spread.
    return {
        dsn: parseDsn(value.dsn),
        ...(environment === undefined ? {} : {environment}),
        ...(release === undefined ? {} : {release}),
        ...(sampleRate === undefined ? {} : {sampleRate}),
        ...(tracesSampleRate === undefined ? {} : {tracesSampleRate}),
    };
};

/**
 * Parses the deployment configuration embedded in the page.
 *
 * The whole configuration is rejected when any part of it is invalid or
 * unsupported, rather than applying the parts that are valid.
 *
 * @param json - the JSON text of the configuration element
 * @returns the configuration
 * @throws TypeError when the configuration is not valid
 *
 * @example
 * parseDeploymentConfig('{"sentry":{"dsn":"https://key@o1.ingest.sentry.io/1"}}');
 * // {sentry: {dsn: 'https://key@o1.ingest.sentry.io/1'}}
 */
export const parseDeploymentConfig = (json: string): DeploymentConfig => {
    if (json.length > MAX_DEPLOYMENT_CONFIG_LENGTH) {
        throw new TypeError(
            `The deployment configuration exceeds ${MAX_DEPLOYMENT_CONFIG_LENGTH} characters`,
        );
    }

    let value: unknown;
    try {
        value = JSON.parse(json);
    } catch (error: unknown) {
        throw new TypeError('The deployment configuration is not valid JSON', {
            cause: error,
        });
    }
    if (!isRecord(value)) {
        throw new TypeError(
            'The deployment configuration must be a JSON object',
        );
    }
    assertKnownKeys(
        value,
        DEPLOYMENT_CONFIG_KEYS,
        'The deployment configuration',
    );

    const hostname = parseOptionalHostname(value.hostname);
    const sentry =
        value.sentry === undefined
            ? undefined
            : parseSentryConfig(value.sentry);

    return {
        ...(hostname === undefined ? {} : {hostname}),
        ...(sentry === undefined ? {} : {sentry}),
    };
};
